import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CropUI from '../components/Crop/CropUI';
import CarouselSimulator from '../components/Carousel/CarouselSimulator';
import AltPanel from '../components/Alt/AltPanel';
import { decodeImage, encodeAllSlices } from '../lib/encode';
import { computeSliceGrid } from '../lib/geometry';
import { publishGallery } from '../lib/publish';
import { verifyPost, deletePost } from '../lib/verify';

const Home = () => {
  const { session, agent, logout, loading } = useAuth();
  const [source, setSource] = useState(null); // decoded ImageBitmap
  const [decodeError, setDecodeError] = useState(null);
  const [crop, setCrop] = useState(null);
  const [n, setN] = useState(5);
  const [blobs, setBlobs] = useState(null);
  const [sliceDims, setSliceDims] = useState(null); // {width, height} from encode
  const [encodeState, setEncodeState] = useState('idle'); // idle|encoding|done|overLimit|error
  const [encodeError, setEncodeError] = useState(null);
  const [alts, setAlts] = useState([]);
  const [altsValid, setAltsValid] = useState(false);
  const [labels, setLabels] = useState([]);
  const [publishState, setPublishState] = useState('idle'); // idle|publishing|verifying|done|deleted|error
  const [publishResult, setPublishResult] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const encodeTimerRef = useRef(null);

  // Red-team SEC-02: cap the source file size. Huge sources (or
  // decompression bombs) OOM low-end devices at decode time; the crop path
  // never needs sources this large.
  const MAX_SOURCE_BYTES = 50 * 1024 * 1024; // 50MB

  const onFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    setDecodeError(null);
    setBlobs(null);
    setCrop(null);
    setEncodeState('idle');
    setPublishState('idle');
    setPublishResult(null);

    if (!file) return;

    if (file.size > MAX_SOURCE_BYTES) {
      setDecodeError(
        `Image is ${(file.size / (1024 * 1024)).toFixed(0)}MB — Slicer accepts up to 50MB. Use a smaller source.`
      );

      return;
    }

    try {
      const bitmap = await decodeImage(file);
      // Red-team PERF-01: release the previous bitmap's decoded memory.
      setSource((prev) => {
        if (prev && prev !== bitmap) prev.close?.();

        return bitmap;
      });
    } catch (err) {
      console.error('Decode failed:', err);
      setDecodeError('Could not decode that image. Use a JPEG, PNG, or WebP panorama.');
    }
  }, []);

  // Debounced encode: re-encode the current crop's N slices whenever the
  // crop or slice count changes, feeding the carousel simulator.
  useEffect(() => {
    if (!source || !crop) {
      setBlobs(null);
      setEncodeState('idle');

      return;
    }

    setEncodeState('encoding');
    clearTimeout(encodeTimerRef.current);
    encodeTimerRef.current = setTimeout(async () => {
      try {
        const grid = computeSliceGrid({
          srcWidth: source.width,
          srcHeight: source.height,
          cropRect: crop,
          n,
        });

        const result = await encodeAllSlices({
          source,
          sliceRects: grid.slices,
          targetLongEdge: 2000,
        });

        setBlobs(result.blobs);
        setSliceDims({ width: result.width, height: result.height });
        setEncodeState(result.overLimit ? 'overLimit' : 'done');
        setEncodeError(null);
      } catch (err) {
        console.error('Encode failed:', err);
        setEncodeState('error');
        setEncodeError('Encoding failed: ' + (err.message || err));
      }
    }, 300);

    return () => clearTimeout(encodeTimerRef.current);
  }, [source, crop, n]);

  const onCropChange = useCallback((newCrop, newN) => {
    setCrop(newCrop);

    if (newN) setN(newN);
  }, []);

  const onAltsChange = useCallback((nextAlts, valid) => {
    setAlts(nextAlts);
    setAltsValid(valid);
  }, []);

  const onLabelsChange = useCallback((nextLabels) => {
    setLabels(nextLabels);
  }, []);

  const onPublish = useCallback(async () => {
    if (!agent || !session?.did || !blobs || !sliceDims || !altsValid) return;
    setPublishState('publishing');
    setPublishError(null);
    setProgress({ done: 0, total: blobs.length });

    try {
      const result = await publishGallery({
        agent,
        did: session.did,
        blobs,
        alts,
        aspectRatio: sliceDims,
        text: '', // post text comes later (M8 scope: gallery embed + alt)
        labels,
        onProgress: (done, total) => setProgress({ done, total }),
        pacing: { interRequestDelayMs: 250 },
      });

      setPublishResult(result);

      // M9: post-publish verification (plan Step 8) — poll the AppView
      // until the gallery embed hydrates with N items.
      setPublishState('verifying');

      const verification = await verifyPost({
        agent,
        uri: result.uri,
        expectedItems: blobs.length,
        onAttempt: (attempt, analysis) => {
          // CDN preset discovery (§13 Q3): log observed presets in dev.
          if (process.env.NODE_ENV === 'development' && analysis.observedPresets?.length) {
            console.log(`[slicer] verify attempt ${attempt}: presets=${analysis.observedPresets.join(',')} items=${analysis.itemCount}`);
          }
        },
      });

      if (verification.verified) {
        setPublishState('done');
      } else {
        // D2 (owner option a): record created but embed did not hydrate —
        // auto-delete so no broken public post is left behind.
        await deletePost({ agent, did: session.did, uri: result.uri });
        setPublishState('deleted');
        setPublishError(
          'The post was created but did not verify as a gallery embed (the embed may not be live on the AppView yet). It was automatically deleted — no cleanup needed.'
        );
      }
    } catch (err) {
      console.error('Publish failed:', err);
      // Plan Step 7: on mid-upload failure we ABANDON — orphan blobs are
      // GC'd by the PDS (blob lifetime is tied to record references).
      // Surface that as "no cleanup needed" rather than alarming the user.
      setPublishError(
        `Publishing failed: ${err.message || err}. No cleanup needed — unreferenced uploads are garbage-collected by your PDS.`
      );
      setPublishState('error');
    }
  }, [agent, session, blobs, sliceDims, altsValid, alts, labels]);

  if (loading) return <p>Loading...</p>;

  return (
    <div className="home-page">
      <header className="topbar">
        <h1>Slicer</h1>
        <div className="account">
          <span>{session?.did || ''}</span>
          <button type="button" onClick={logout}>Log out</button>
        </div>
      </header>

      <section className="panel">
        <h2>Panorama</h2>
        <p>Upload a wide (or tall) image, choose slice count and aspect, then position the crop.</p>
        <input
          type="file"
          accept="image/*"
          onChange={onFile}
          aria-label="Choose panorama image"
        />
        {decodeError && <p className="crop-error">{decodeError}</p>}
        {source && (
          <CropUI image={source} onCropChange={onCropChange} />
        )}
      </section>

      {blobs && (
        <section className="panel">
          <h2>Preview</h2>
          {encodeState === 'overLimit' && (
            <p className="crop-error">
              Slices still exceed 2MB after maximum downscaling — publishing is blocked.
            </p>
          )}
          {encodeState === 'error' && <p className="crop-error">{encodeError}</p>}
          <CarouselSimulator blobs={blobs} />
        </section>
      )}

      {blobs && encodeState === 'done' && (
        <section className="panel">
          <AltPanel
            total={n}
            onAltsChange={onAltsChange}
            onLabelsChange={onLabelsChange}
            onPublish={onPublish}
          />
          {publishState === 'publishing' && (
            <p aria-live="polite">
              Publishing {progress.done}/{progress.total}...
            </p>
          )}
          {publishState === 'verifying' && (
            <p aria-live="polite">Verifying the post on Bluesky...</p>
          )}
          {publishState === 'error' && (
            <p className="crop-error" role="alert">{publishError}</p>
          )}
          {publishState === 'deleted' && (
            <p className="crop-error" role="alert">{publishError}</p>
          )}
          {publishState === 'done' && publishResult && (
            <div className="publish-result" role="status">
              <p>Published and verified!</p>
              <p>
                <a href={publishResult.permalink} target="_blank" rel="noreferrer">
                  View on Bluesky
                </a>
              </p>
              <p className="alt-note">{publishResult.uri}</p>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default Home;
